/**
 * cncEngine.js — CNC toolpath + modular interior system
 *
 * Capabilities:
 *   1. Toolpath generation — converts geometry to G-code
 *   2. Kerf compensation — offsets paths inward/outward by tool radius
 *   3. Sheet nesting — fits panels onto standard sheet sizes
 *   4. Modular panel library — cabinet parts with standard dimensions
 *   5. Cut list CSV export — panel-by-panel cut sheet
 *   6. G-code export — ready for CNC router / laser cutter
 *
 * All coordinates stay in mm internally.
 * G-code uses mm (G21) with absolute positioning (G90).
 *
 * Supported machines:
 *   - CNC router (3-axis milling)
 *   - Laser cutter (2D cut/engrave)
 *   - Plasma cutter
 */

import { Geo } from "../utils/geo";
import { splineToPath } from "./primitives";

// ─── G-code comment sanitizer ────────────────────────────────────────────────
// Newlines, semicolons, and % inside G-code comments can inject new commands.
// Strip all of them from any user-supplied string before embedding in G-code.
const safeLabel = (str) => String(str || "").replace(/[\r\n;%\x00-\x1F]/g, "_").slice(0, 64);

export const CNC_MACHINES = {
  router: {
    id: "router", label: "CNC Router",
    feedRate:    3000,    // mm/min cutting
    plungeRate:   600,    // mm/min plunge
    spindleRPM: 18000,
    toolDia:       6,    // mm default end mill
    safeZ:        10,    // mm safe height
    cutDepth:      3,    // mm per pass
    comment: "; CNC Router G-code — 3-axis milling",
  },
  laser: {
    id: "laser", label: "Laser Cutter",
    feedRate:    6000,
    power:         90,   // % power for cut
    engravePower:  30,
    passes:         1,
    comment: "; Laser Cutter G-code (Grbl)",
  },
  plasma: {
    id: "plasma", label: "Plasma Cutter",
    feedRate:    4000,
    pierceDelay:  0.5,   // seconds
    comment: "; Plasma Cutter G-code",
  },
};

// ─── Sheet sizes ──────────────────────────────────────────────────────────────

export const SHEET_SIZES = {
  "8x4":     { label: "8×4 ft (2440×1220mm)", w: 2440, h: 1220 },
  "6x3":     { label: "6×3 ft (1830×915mm)",  w: 1830, h:  915 },
  "2400x1200":{ label: "2400×1200mm",          w: 2400, h: 1200 },
  "1200x600": { label: "1200×600mm",           w: 1200, h:  600 },
  "3000x1500":{ label: "3000×1500mm",          w: 3000, h: 1500 },
};

// ─── Modular interior panel library ──────────────────────────────────────────

export const PANEL_TYPES = {
  // Cabinet carcass
  side_panel:    { id:"side_panel",    label:"Side panel",       category:"carcass", thickness:18 },
  top_panel:     { id:"top_panel",     label:"Top panel",        category:"carcass", thickness:18 },
  bottom_panel:  { id:"bottom_panel",  label:"Bottom panel",     category:"carcass", thickness:18 },
  back_panel:    { id:"back_panel",    label:"Back panel",       category:"carcass", thickness: 6 },
  shelf:         { id:"shelf",         label:"Shelf",            category:"carcass", thickness:18 },
  // Door/drawer fronts
  door:          { id:"door",          label:"Door front",       category:"front",   thickness:18 },
  drawer_front:  { id:"drawer_front",  label:"Drawer front",     category:"front",   thickness:18 },
  // Structural
  fixed_shelf:   { id:"fixed_shelf",   label:"Fixed shelf",      category:"struct",  thickness:18 },
  partition:     { id:"partition",     label:"Partition",        category:"struct",  thickness:18 },
  // CNC-specific
  routed_panel:  { id:"routed_panel",  label:"Routed panel",     category:"cnc",     thickness:18 },
  perforated:    { id:"perforated",    label:"Perforated panel", category:"cnc",     thickness:18 },
};

// ─── Kerf compensation ────────────────────────────────────────────────────────

/**
 * Offset a closed polygon path inward (-) or outward (+) by kerfRadius.
 * Simple vertex-by-vertex normal offset. Adequate for rectangular panels.
 */
export function kerfCompensate(pts, kerfRadius, side = "inside") {
  const dir = side === "inside" ? -1 : 1;
  const n = pts.length;
  const result = [];

  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const curr = pts[i];
    const next = pts[(i + 1) % n];

    const d1x = curr.x - prev.x, d1y = curr.y - prev.y;
    const d2x = next.x - curr.x, d2y = next.y - curr.y;
    const L1  = Math.sqrt(d1x*d1x + d1y*d1y) || 1;
    const L2  = Math.sqrt(d2x*d2x + d2y*d2y) || 1;

    // Inward normal of each edge
    const n1 = { x: d1y/L1 * dir, y: -d1x/L1 * dir };
    const n2 = { x: d2y/L2 * dir, y: -d2x/L2 * dir };

    // Average normal at vertex
    const nx = (n1.x + n2.x) / 2, ny = (n1.y + n2.y) / 2;
    const nLen = Math.sqrt(nx*nx + ny*ny) || 1;

    result.push({
      x: curr.x + (nx/nLen) * kerfRadius,
      y: curr.y + (ny/nLen) * kerfRadius,
    });
  }
  return result;
}

// ─── G-code generation ────────────────────────────────────────────────────────

function gLine(x, y) {
  return `G1 X${x.toFixed(3)} Y${y.toFixed(3)}`;
}
function gRapid(x, y, z) {
  return z !== undefined
    ? `G0 X${x.toFixed(3)} Y${y.toFixed(3)} Z${z.toFixed(3)}`
    : `G0 X${x.toFixed(3)} Y${y.toFixed(3)}`;
}

function routerHeader(machine) {
  return [
    machine.comment,
    `; Generated by DPL Jade v7 — ${new Date().toISOString()}`,
    "G21 ; mm",
    "G90 ; absolute",
    "G17 ; XY plane",
    `S${machine.spindleRPM} M3 ; spindle on`,
    `G0 Z${machine.safeZ}`,
  ].join("\n");
}

function routerFooter(machine) {
  return [
    `G0 Z${machine.safeZ}`,
    "M5 ; spindle off",
    "G0 X0 Y0",
    "M30 ; end program",
  ].join("\n");
}

function laserHeader(machine) {
  return [
    machine.comment,
    `; Generated by DPL Jade v7 — ${new Date().toISOString()}`,
    "G21 ; mm",
    "G90 ; absolute",
    "M5 ; laser off",
    "G0 X0 Y0",
  ].join("\n");
}

function laserFooter() {
  return ["M5 ; laser off", "G0 X0 Y0", "M30"].join("\n");
}

/**
 * Generate G-code for cutting a closed polygon (panel outline).
 * Handles multi-pass depth for routers.
 */
function gcodePolygon(pts, machine, options = {}) {
  const { depth = 18, tabWidth = 5, tabHeight = 2 } = options;
  const lines = [];

  if (machine.id === "router") {
    const passes = Math.ceil(depth / machine.cutDepth);
    const firstPt = pts[0];

    for (let pass = 1; pass <= passes; pass++) {
      const z = -Math.min(pass * machine.cutDepth, depth);
      lines.push(`; Pass ${pass}/${passes} depth Z${z.toFixed(2)}`);
      lines.push(gRapid(firstPt.x, firstPt.y, machine.safeZ));
      lines.push(`G1 Z${z.toFixed(3)} F${machine.plungeRate}`);
      lines.push(`F${machine.feedRate}`);
      for (let i = 1; i < pts.length; i++) {
        lines.push(gLine(pts[i].x, pts[i].y));
      }
      lines.push(gLine(firstPt.x, firstPt.y)); // close
      lines.push(gRapid(firstPt.x, firstPt.y, machine.safeZ));
    }
  } else if (machine.id === "laser") {
    const firstPt = pts[0];
    for (let p = 0; p < (machine.passes || 1); p++) {
      lines.push(`; Pass ${p+1}`);
      lines.push(gRapid(firstPt.x, firstPt.y));
      lines.push(`M4 S${Math.round(machine.power * 10)} ; laser on`);
      lines.push(`G1 F${machine.feedRate}`);
      for (let i = 1; i < pts.length; i++) {
        lines.push(gLine(pts[i].x, pts[i].y));
      }
      lines.push(gLine(firstPt.x, firstPt.y));
      lines.push("M5 ; laser off");
    }
  }

  return lines.join("\n");
}

/**
 * Generate G-code for a circle (drill / pocket / cut).
 */
function gcodeCircle(cx, cy, r, machine, options = {}) {
  const { mode = "cut", depth = 5 } = options;
  const lines = [];

  if (machine.id === "laser") {
    // Laser: arc at radius
    const startX = cx + r, startY = cy;
    lines.push(gRapid(startX, startY));
    lines.push(`M4 S${Math.round(machine.power * 10)}`);
    lines.push(`G2 X${startX.toFixed(3)} Y${startY.toFixed(3)} I${(-r).toFixed(3)} J0 F${machine.feedRate}`);
    lines.push("M5");
  } else {
    // Router: helical drill or arc pass
    const startX = cx + r;
    const z = -depth;
    lines.push(gRapid(startX, cy, machine.safeZ));
    lines.push(`G1 Z${z.toFixed(3)} F${machine.plungeRate}`);
    lines.push(`G2 X${startX.toFixed(3)} Y${cy.toFixed(3)} I${(-r).toFixed(3)} J0 F${machine.feedRate}`);
    lines.push(gRapid(startX, cy, machine.safeZ));
  }

  return lines.join("\n");
}

// ─── Main G-code export ───────────────────────────────────────────────────────

/**
 * exportGCode(st, machineId, options)
 *
 * Converts all geometry in the current DCEL state + primitives to G-code.
 * Applies kerf compensation based on machine tool diameter.
 *
 * options: {
 *   kerf: boolean (default true),
 *   sheetW, sheetH: sheet dimensions for bounds check,
 *   materialThickness: mm (default 18),
 *   selectedOnly: [entityId, ...] — if set, only export these
 * }
 */
export function exportGCode(st, machineId = "router", options = {}) {
  const machine = CNC_MACHINES[machineId] || CNC_MACHINES.router;
  const {
    kerf = true,
    materialThickness = 18,
    selectedOnly = null,
  } = options;

  const kerfR = kerf ? machine.toolDia / 2 : 0;
  const lines = [];

  // Header
  lines.push(machine.id === "laser" ? laserHeader(machine) : routerHeader(machine));
  lines.push("");

  let partCount = 0;

  // ── Walls as rectangular profiles ─────────────────────────────────────────
  for (const edge of Object.values(st.edges || {})) {
    if (selectedOnly && !selectedOnly.includes(edge.id)) continue;
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;

    const len   = Geo.d(nA, nB);
    const thick = edge.thickness || 18;
    if (len < 1) continue;

    partCount++;
    lines.push(`; Panel ${partCount} — ${safeLabel(edge.label || edge.id.slice(0,8))} ${Math.round(len)}x${thick}mm`);

    // Flat panel: place at origin, len=X, thick=Y
    let pts = [
      { x: 0,   y: 0 },
      { x: len, y: 0 },
      { x: len, y: thick },
      { x: 0,   y: thick },
      { x: 0,   y: 0 },
    ];

    if (kerfR > 0) {
      const compensated = kerfCompensate(pts.slice(0,-1), kerfR, "inside");
      pts = [...compensated, compensated[0]];
    }

    lines.push(gcodePolygon(pts, machine, { depth: materialThickness }));
    lines.push("");
  }

  // ── Circles (drill / cut) ─────────────────────────────────────────────────
  for (const c of Object.values(st.circles || {})) {
    if (selectedOnly && !selectedOnly.includes(c.id)) continue;
    partCount++;
    lines.push(`; Circle cut — r=${Math.round(c.r)}mm id=${safeLabel(c.id.slice(0,8))}`);
    const r = kerf ? Math.max(0.1, c.r - kerfR) : c.r;
    lines.push(gcodeCircle(c.cx, c.cy, r, machine, { depth: materialThickness }));
    lines.push("");
  }

  // ── Polylines (custom panel profiles) ─────────────────────────────────────
  for (const pl of Object.values(st.polylines || {})) {
    if (selectedOnly && !selectedOnly.includes(pl.id)) continue;
    if (pl.pts.length < 2) continue;
    partCount++;
    lines.push(`; Polyline cut — ${pl.pts.length} pts id=${safeLabel(pl.id.slice(0,8))}`);
    let pts = [...pl.pts];
    if (pl.closed && kerfR > 0) {
      pts = kerfCompensate(pts, kerfR, "inside");
    }
    if (pl.closed) pts = [...pts, pts[0]];
    lines.push(gcodePolygon(pts, machine, { depth: materialThickness }));
    lines.push("");
  }

  // Footer
  lines.push(machine.id === "laser" ? laserFooter() : routerFooter(machine));

  return {
    gcode:      lines.join("\n"),
    partCount,
    machine:    machine.label,
    timestamp:  new Date().toISOString(),
  };
}

// ─── Cut list CSV ─────────────────────────────────────────────────────────────

/**
 * exportCutList(st)
 * Returns a CSV string listing every panel with dimensions, material, qty.
 */
export function exportCutList(st) {
  const rows = [
    ["Part","Label","Length (mm)","Width (mm)","Thickness (mm)","Material","Qty","Edge banding","Notes"]
  ];

  let n = 1;
  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    const len = Math.round(Geo.d(nA, nB));
    if (len < 1) continue;

    const panelType = edge.panelType || "panel";
    const mat = edge.materialId || "MDF";
    const thick = edge.thickness || 18;
    const height = edge.height || 720;
    const edgeBand = edge.edgeBanding || "";
    const label = safeLabel(edge.label || `Panel ${n}`);

    rows.push([n, label, len, height, thick, mat, 1, edgeBand, panelType]);
    n++;
  }

  // Circles as disc cutouts
  for (const c of Object.values(st.circles || {})) {
    rows.push([n, `Circle Ø${Math.round(c.r*2)}`, Math.round(c.r*2), Math.round(c.r*2), 18, "MDF", 1, "", "disc/hole"]);
    n++;
  }

  return rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
}

// ─── Sheet nesting (simple bin-packing) ──────────────────────────────────────

/**
 * nestPanels(panels, sheetW, sheetH, margin)
 *
 * panels: [{ id, w, h, label }]
 * Returns: { placements: [{ id, x, y, rotated }], sheetsUsed, efficiency }
 *
 * Uses First Fit Decreasing Height (FFDH) shelf algorithm.
 * Good for rectangular parts. Sufficient for modular interior panels.
 */
export function nestPanels(panels, sheetW = 2440, sheetH = 1220, margin = 10) {
  // Sort by height descending
  const sorted = [...panels].sort((a, b) => b.h - a.h);

  const placements = [];
  let sheetX = margin, sheetY = margin, rowH = 0;
  let sheetsUsed = 1, totalArea = 0;

  for (const panel of sorted) {
    const pw = panel.w + margin;
    const ph = panel.h + margin;
    totalArea += panel.w * panel.h;

    // Try to fit in current row
    if (sheetX + pw <= sheetW - margin) {
      placements.push({ id: panel.id, label: panel.label, x: sheetX, y: sheetY, w: panel.w, h: panel.h, rotated: false, sheet: sheetsUsed });
      sheetX += pw;
      rowH = Math.max(rowH, ph);
    } else {
      // Next row
      sheetY += rowH;
      rowH = ph;
      sheetX = margin;

      if (sheetY + ph > sheetH - margin) {
        // Next sheet
        sheetsUsed++;
        sheetX = margin; sheetY = margin; rowH = ph;
      }

      placements.push({ id: panel.id, label: panel.label, x: sheetX, y: sheetY, w: panel.w, h: panel.h, rotated: false, sheet: sheetsUsed });
      sheetX += pw;
    }
  }

  const sheetArea = sheetsUsed * sheetW * sheetH;
  const efficiency = totalArea > 0 ? Math.round(totalArea / sheetArea * 100) : 0;

  return { placements, sheetsUsed, efficiency, sheetW, sheetH };
}

/**
 * Builds a panel list from the current DCEL state for nesting.
 */
export function buildPanelList(st) {
  const panels = [];
  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    const len = Math.round(Geo.d(nA, nB));
    const h   = edge.height || 720;
    if (len < 1) continue;
    panels.push({ id: edge.id, w: len, h, label: edge.label || edge.id.slice(0,8) });
  }
  return panels;
}

// ─── Modular interior presets ─────────────────────────────────────────────────

/**
 * Cabinet module generator — returns a set of DCEL mutations
 * to draw the elevation view of a standard cabinet.
 *
 * type: "base" | "wall" | "tall" | "drawer" | "tv_unit"
 */
export function cabinetModulePts(type, w, h, depth) {
  // Returns outline + internal shelf positions for elevation view
  const modules = {
    base: {
      outline:  [{ x:0,y:0 },{ x:w,y:0 },{ x:w,y:h },{ x:0,y:h }],
      shelves:  [],
      toe:      { y: 100, h: 100 },
      doors:    [{ x:10, y:110, w:w-20, h:h-120 }],
    },
    wall: {
      outline:  [{ x:0,y:0 },{ x:w,y:0 },{ x:w,y:h },{ x:0,y:h }],
      shelves:  [{ y: h*0.33 }, { y: h*0.66 }],
      doors:    [{ x:10, y:10, w:w-20, h:h-20 }],
    },
    tall: {
      outline:  [{ x:0,y:0 },{ x:w,y:0 },{ x:w,y:h },{ x:0,y:h }],
      shelves:  [{ y:h*0.25 },{ y:h*0.5 },{ y:h*0.75 }],
      doors:    [{ x:10,y:10,w:w-20,h:h/2-15 },{ x:10,y:h/2+5,w:w-20,h:h/2-15 }],
    },
    drawer: {
      outline:  [{ x:0,y:0 },{ x:w,y:0 },{ x:w,y:h },{ x:0,y:h }],
      shelves:  [{ y:h*0.33 },{ y:h*0.66 }],
      drawers:  [{ x:10,y:10,w:w-20,h:h*0.3 },{ x:10,y:h*0.33+10,w:w-20,h:h*0.3 },{ x:10,y:h*0.66+10,w:w-20,h:h*0.28 }],
    },
    tv_unit: {
      outline:  [{ x:0,y:0 },{ x:w,y:0 },{ x:w,y:h },{ x:0,y:h }],
      shelves:  [{ y:h*0.5 }],
      openBays: [{ x:10,y:h*0.5+10,w:w*0.4-15,h:h*0.45 },{ x:w*0.4+5,y:h*0.5+10,w:w*0.6-15,h:h*0.45 }],
    },
  };
  return modules[type] || modules.base;
}

// ─── Download helpers ─────────────────────────────────────────────────────────

export function downloadGCode(st, machineId, options, filename) {
  const { gcode, partCount } = exportGCode(st, machineId, options);
  const blob = new Blob([gcode], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || `jade-cnc-${machineId}-${Date.now()}.nc`;
  a.click();
  URL.revokeObjectURL(a.href);
  return partCount;
}

export function downloadCutList(st, filename) {
  const csv = exportCutList(st);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || `jade-cutlist-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
